/**
 * Centralized tunable constants. Avoid magic numbers elsewhere in the codebase.
 */

// Session composition (must sum to 1.0). The composer fills buckets in
// priority order: as many UNSEEN items as the unseen pool allows up to
// the target, then wrong-pool items up to their target, then mastered
// items (sorted by nextReview ASC so "haven't seen in a while" items
// surface first). Tuning rationale: kids who have only mastered a
// fraction of the pool should see MOSTLY new vocabulary, with a
// smaller targeted dose of past mistakes and an even smaller dose of
// spaced-repetition refreshers — directly addresses the "ίδιες λέξεις
// επαναλαμβάνονται" feedback. The earlier shape `{review, weakness,
// unseen}` was aspirational only — composeSessionByMistakes never
// actually honoured those ratios.
export const SESSION_COMPOSITION = {
  unseen: 0.6, // 6/10 — bulk of the session is fresh exposure
  wrongPool: 0.3, // 3/10 — targeted re-attempts at past mistakes
  masteredRecycle: 0.1, // 1/10 — light spaced-repetition refresher
};

export const SESSION_SIZE = 10;

// Soft cap on the share of a single session that may come from grades
// SMALLER than the one the child chose. The composer prioritises items
// of the current grade and falls through to lower grades only when the
// cap allows it OR when no current-grade material remains (slack-fill
// passes ignore the cap to avoid half-empty sessions). At SESSION_SIZE
// = 10 and ratio 0.2 this allows up to ~2 revision items per session.
export const LOWER_GRADE_MAX_RATIO = 0.2;

// Maximum attempts per question before revealing the answer + rule explanation.
export const MAX_ATTEMPTS = 3;

// XP awards
export const XP = {
  CORRECT_FIRST_ATTEMPT: 10,
  CORRECT_SECOND_ATTEMPT: 5,
  WRONG: 0,
  PERFECT_SESSION_BONUS: 50,
  DAILY_LOGIN: 20,
};

// SM-2 algorithm parameters
export const SM2 = {
  INITIAL_EASE: 2.5,
  MIN_EASE: 1.3,
  EASE_INCREMENT: 0.1,
  EASE_DECREMENT: 0.2,
  // Quality thresholds (0-5 scale internally, but we use binary correct/wrong)
  CORRECT_QUALITY: 4,
  WRONG_QUALITY: 1,
};

// Weakness profile decay
export const WEAKNESS_DECAY = 0.9; // older errors weigh 0.9x at each new error event
export const WEAKNESS_MAX_SCORE = 1.0;
export const WEAKNESS_MIN_SCORE = 0.0;

// Streak milestones that trigger badge checks
export const STREAK_MILESTONES = [3, 7, 14, 30];

// Storage keys
//
// NOTE: The `_v1` suffix is historical — the actual schema version lives
// inside the stored object under `schemaVersion`. Bumping that field rather
// than renaming the key keeps the migration path explicit and additive.
export const STORAGE_KEYS = {
  PROGRESS: 'lexonauts__progress_v1',
  // One-shot backup of the previous progress shape, written exactly when
  // hydration migrates from an older schema. Lets us recover user data if
  // a future migration step turns out to be wrong.
  PROGRESS_BACKUP: 'lexonauts__progress_backup',
  ITEM_TRACKING: 'lexonauts__item_tracking_v1',
  SETTINGS: 'lexonauts__settings_v1',
  LAST_SESSION_DATE: 'lexonauts__last_session_date_v1',
  // Records the child/parent's choice at the first-run sign-in gateway:
  // 'guest' (play offline, no sync) or 'account' (signed in). Only consulted
  // when SYNC_ENABLED is true — the offline-only build never shows the gateway.
  ENTRY_CHOICE: 'lexonauts__entry_choice_v1',
  // Which learning profile is currently playing on THIS device (a server
  // profile id). Points at which namespaced progress blob to load:
  //   null         → the legacy PROGRESS key (single offline/guest profile)
  //   <profileId>  → `${PROGRESS}::<profileId>` (per-child, for siblings)
  // Only set when SYNC_ENABLED + signed in, so the offline-only build keeps
  // using the legacy key and stays byte-for-byte unchanged.
  ACTIVE_PROFILE: 'lexonauts__active_profile_v1',
};

// ── Sync feature flag ──────────────────────────────────────────────────────
// Master switch for the (in-development) online sync layer. While FALSE the
// app behaves exactly as the offline-only version ("offline-independent" mode):
// no identity generation, no outbox emission, no network — the v8 sync fields
// just sit dormant.
//
// PARAMETRIC: driven by the VITE_SYNC_ENABLED build-time env var so the offline
// vs. online behaviour can be flipped without touching code (set it in
// .env.development.local for local testing). Defaults to FALSE, so an
// unconfigured build is byte-for-byte the offline-only app. The offline-first
// architecture is preserved either way: even with sync ON, the local store
// stays the runtime source of truth and the sync layer only reconciles with
// the server in the background.
export const SYNC_ENABLED = (import.meta.env?.VITE_SYNC_ENABLED ?? 'false') === 'true';

// Base URL of the sync backend. Empty by default (dormant / same-origin). Set
// VITE_API_BASE_URL at build time to point the HTTP client (src/native/api.js)
// at the server, e.g. "https://api.lexonauts.gr". Only consulted while
// SYNC_ENABLED is true; with an empty value requests fall back to relative
// paths (same-origin).
export const SYNC_API_BASE_URL = import.meta.env?.VITE_API_BASE_URL ?? '';

// ── Google sign-in (ready-but-unconnected) ──────────────────────────────────
// OAuth client id for Google Identity Services (GIS). EMPTY by default so the
// Google button is built and wired but stays hidden/disabled until a real id is
// provided via VITE_GOOGLE_CLIENT_ID at build time. The matching audience must
// also be set on the backend (server AUTH_GOOGLE_CLIENT_ID) for verification.
// Only consulted while SYNC_ENABLED is true.
export const GOOGLE_CLIENT_ID = import.meta.env?.VITE_GOOGLE_CLIENT_ID ?? '';

// ── Dev auto-login (local testing only) ──────────────────────────────────────
// Until the real sign-in UI ships, the online path needs *some* authenticated
// session to exercise. When enabled (VITE_DEV_AUTO_LOGIN=true) the boot
// bootstrap (src/native/syncBootstrap.js) auto-registers/logs-in a fixed
// fictional "dev" account, adopts a server learning profile, uploads the local
// baseline, and starts the sync loop — so local tests load/save data as that
// dev user against the (single) backend database.
//
// Only consulted while SYNC_ENABLED is true. NEVER enable this in a production
// build: the credentials live in a gitignored local env file, not in code.
// Hard gate: dev auth exists ONLY in a dev server (`vite dev` → import.meta.env.DEV
// true). Any production build (`vite build`) zeroes it out regardless of env vars,
// so a deploy can NEVER auto-sign-in (or even offer) the fictional dev account.
// To use it locally, set VITE_DEV_AUTO_LOGIN=true in .env.development.local.
const DEV_BUILD = Boolean(import.meta.env?.DEV);
export const DEV_AUTH = {
  enabled: DEV_BUILD && (import.meta.env?.VITE_DEV_AUTO_LOGIN ?? 'false') === 'true',
  email: DEV_BUILD ? (import.meta.env?.VITE_DEV_EMAIL ?? '') : '',
  password: DEV_BUILD ? (import.meta.env?.VITE_DEV_PASSWORD ?? '') : '',
  // Grade for the auto-created dev learning profile (2/3/4); null → fall back to
  // the child's last played grade, else grade 2.
  grade: Number(import.meta.env?.VITE_DEV_GRADE) || null,
};
